import type { IconValue } from "@gettactic/components/src/icons/IconFromSet/IconFromSet.types";
import type { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import type { ShapeEditingHighlightOptions } from "@gettactic/map-builder/src/components/map/types";
import type { Furniture } from "@gettactic/map-builder/src/furniture-list";
import type {
  DrawShape,
  Shape,
  SpotShape,
  ZoneTypeDefinition
} from "@gettactic/map-builder/src/types";
import { createStore, type StoreApi } from "zustand/vanilla";
import {
  addShapes,
  type DocumentState,
  type DuplicateOptions,
  deleteShapes,
  duplicateShapes,
  groupShapes,
  moveShapes,
  reorderShapes,
  ungroupShape,
  updateShape,
  type ZOrderDirection
} from "../document/operations";
import {
  type DocumentMeta,
  type EditorDocument,
  fromMapValue,
  toMapValue
} from "../document/serialize";
import { SpatialIndex } from "../document/spatialIndex";
import type {
  EditorUiState,
  ShapeById,
  Snapshot,
  ToolKind,
  ToolState,
  Viewport
} from "../types";

const HISTORY_LIMIT = 100;

/** Editor UI state the existing panels and toolbars read through the
 *  legacy contexts: picker selections and transient targets. */
export interface ChromeState {
  iconToolSelectedIcon: IconValue | null;
  furnitureToolSelectedFurniture: Furniture | null;
  zoneToolTag: ZoneTypeDefinition | null;
  highlightedShape: ShapeEditingHighlightOptions | null;
  contextMenuShape: Shape | null;
  nodeStyleToolTarget: SpotShape | null;
  wallPathSelectedVertexIndex: number | null;
  /** Scale dialog: the measured line's length, or nothing when opened
   *  from the menu to calibrate from the building's floor area. */
  pendingMeasurement: { pixelLength?: number } | null;
  /** The canvas shows the viewer's rendering instead of the editor. */
  isPreview: boolean;
  /** Grid tool rectangle awaiting rows/columns. */
  pendingGrid: { x: number; y: number; width: number; height: number } | null;
  /** Rotation of the multi-selection's frame, accumulated by group
   *  rotations so the box stays with the shapes; cleared with the selection. */
  selectionFrameRotation: number;
  /** Rooms picked with the auto-zone tool; each pick becomes one zone
   *  when approved. */
  autoZonePicks: AutoZonePick[];
}

/** One future zone in the room picker. */
export interface AutoZonePick {
  /** Indices into the detected room polygons. */
  rooms: number[];
  /** Typed name; null means the automatic one. */
  name: string | null;
  /** Organisation zone the region belongs to. */
  tag: ZoneTypeDefinition | null;
}

export interface EditorState {
  shapes: ShapeById;
  order: string[];
  meta: DocumentMeta;
  selection: string[];
  tool: ToolState;
  viewport: Viewport;
  ui: EditorUiState;
  history: { past: Snapshot[]; future: Snapshot[] };
  isDirty: boolean;
  /** Document fragment; ids are regenerated on paste. */
  clipboard: Shape[];
  chrome: ChromeState;
  /** Off while inline editors type, so a burst of edits is one history entry. */
  isHistoryRecording: boolean;

  loadDocument: (value?: MapCanvasValue) => void;
  getMapValue: () => MapCanvasValue;
  /** Bounds index for the current shapes; memoised on `shapes` identity. */
  getIndex: () => SpatialIndex;

  /** Applies a named document operation. Every user edit goes through here
   *  so history and the dirty flag stay consistent. */
  commit: (
    mutate: (document: DocumentState) => DocumentState,
    options?: { history?: boolean; selection?: string[] }
  ) => void;
  setSelection: (ids: string[]) => void;
  setTool: (kind: ToolKind, drawShape?: DrawShape) => void;
  setViewport: (viewport: Viewport) => void;
  setUi: (patch: Partial<EditorUiState>) => void;
  setMeta: (patch: Partial<DocumentMeta>) => void;
  setChrome: (patch: Partial<ChromeState>) => void;
  setHistoryRecording: (isRecording: boolean) => void;
  markSaved: () => void;
  undo: () => void;
  redo: () => void;

  addShapes: (shapes: Shape[], options?: { select?: boolean }) => void;
  updateShape: (
    id: string,
    patch: Partial<Shape>,
    options?: { history?: boolean }
  ) => void;
  moveShapes: (ids: string[], dx: number, dy: number) => void;
  deleteSelection: () => void;
  duplicateSelection: (offset?: { x: number; y: number }) => string[];
  duplicateShapes: (ids: string[], options?: DuplicateOptions) => string[];
  reorderSelection: (direction: ZOrderDirection) => void;
  groupSelection: () => void;
  ungroupSelection: () => void;
  toggleLocked: (ids: string[]) => void;
  toggleHidden: (ids: string[]) => void;
  copySelection: () => void;
  pasteClipboard: (offset?: { x: number; y: number }) => string[];
}

export type EditorStore = StoreApi<EditorState>;

const DEFAULT_UI: EditorUiState = {
  showGrid: true,
  snapToGrid: true,
  showSnapLines: true,
  showOutside: true,
  showZoneChecks: true,
  // Off while building; the zone tools switch them on (see `setTool`).
  showZones: false
};

/** Tools that make zones: picking one shows the zones, or the result
 *  would be invisible. */
const ZONE_TOOLS: ToolKind[] = ["zone", "auto-zone", "zone-polygon"];

const snapshotOf = (state: EditorState): Snapshot => ({
  shapes: state.shapes,
  order: state.order,
  selection: state.selection
});

export const createEditorStore = (
  initialValue?: MapCanvasValue,
  options: {
    initialTool?: ToolKind;
    initialZoneToolTag?: ZoneTypeDefinition | null;
    /** UI toggles to start from (a viewer shows zones, the editor hides
     *  them while building). */
    initialUi?: Partial<EditorUiState>;
    /** Where the camera starts; the host restores the map page's position. */
    initialViewport?: Viewport;
  } = {}
): EditorStore => {
  const initial = fromMapValue(initialValue);
  const startTool: ToolKind = options.initialTool ?? "select";
  let index: SpatialIndex | null = null;
  let indexedShapes: ShapeById | null = null;

  return createStore<EditorState>((set, get) => ({
    shapes: initial.shapes,
    order: initial.order,
    meta: initial.meta,
    selection: [],
    tool: { kind: startTool, drawShape: "rectangle" },
    viewport: options.initialViewport ?? { x: 0, y: 0, scale: 1 },
    ui: {
      ...DEFAULT_UI,
      // Opening on a zone tool (Settings' "Draw on a floor") shows the zones,
      // or the region drawn next would be invisible.
      ...(ZONE_TOOLS.includes(startTool) || options.initialZoneToolTag
        ? { showZones: true }
        : {}),
      ...options.initialUi
    },
    history: { past: [], future: [] },
    isDirty: false,
    clipboard: [],
    chrome: {
      iconToolSelectedIcon: null,
      furnitureToolSelectedFurniture: null,
      zoneToolTag: options.initialZoneToolTag ?? null,
      highlightedShape: null,
      contextMenuShape: null,
      nodeStyleToolTarget: null,
      wallPathSelectedVertexIndex: null,
      pendingMeasurement: null,
      pendingGrid: null,
      selectionFrameRotation: 0,
      autoZonePicks: [],
      isPreview: false
    },
    isHistoryRecording: true,

    loadDocument: (value) => {
      const document: EditorDocument = fromMapValue(value);
      set({
        shapes: document.shapes,
        order: document.order,
        meta: document.meta,
        selection: [],
        history: { past: [], future: [] },
        isDirty: false
      });
    },

    getMapValue: () => {
      const { shapes, order, meta } = get();
      return toMapValue({ shapes, order, meta });
    },

    getIndex: () => {
      const { shapes, order } = get();
      if (!index || indexedShapes !== shapes) {
        index = new SpatialIndex(shapes, order);
        indexedShapes = shapes;
      }
      return index;
    },

    commit: (mutate, commitOptions = {}) => {
      const state = get();
      const next = mutate({ shapes: state.shapes, order: state.order });
      const selection = commitOptions.selection ?? state.selection;
      if (
        next.shapes === state.shapes &&
        next.order === state.order &&
        selection === state.selection
      ) {
        return;
      }
      const recordHistory =
        commitOptions.history !== false && state.isHistoryRecording;
      set({
        shapes: next.shapes,
        order: next.order,
        selection,
        isDirty: recordHistory ? true : state.isDirty,
        history: recordHistory
          ? {
              past: [...state.history.past, snapshotOf(state)].slice(
                -HISTORY_LIMIT
              ),
              future: []
            }
          : state.history
      });
    },

    setSelection: (ids) => {
      const current = get().selection;
      if (
        current.length === ids.length &&
        current.every((id, position) => id === ids[position])
      ) {
        return;
      }
      set((state) => ({
        selection: ids,
        chrome: { ...state.chrome, selectionFrameRotation: 0 }
      }));
    },

    setTool: (kind, drawShape) =>
      set((state) => ({
        tool: { kind, drawShape: drawShape ?? state.tool.drawShape },
        ui:
          ZONE_TOOLS.includes(kind) && !state.ui.showZones
            ? { ...state.ui, showZones: true }
            : state.ui
      })),

    setViewport: (viewport) => set({ viewport }),

    setUi: (patch) => set((state) => ({ ui: { ...state.ui, ...patch } })),

    setMeta: (patch) =>
      set((state) => ({ meta: { ...state.meta, ...patch }, isDirty: true })),

    setChrome: (patch) =>
      set((state) => ({ chrome: { ...state.chrome, ...patch } })),

    setHistoryRecording: (isRecording) =>
      set({ isHistoryRecording: isRecording }),

    markSaved: () => set({ isDirty: false }),

    undo: () => {
      const state = get();
      const previous = state.history.past[state.history.past.length - 1];
      if (!previous) return;
      set({
        shapes: previous.shapes,
        order: previous.order,
        selection: previous.selection,
        isDirty: true,
        history: {
          past: state.history.past.slice(0, -1),
          future: [snapshotOf(state), ...state.history.future]
        }
      });
    },

    redo: () => {
      const state = get();
      const [next, ...future] = state.history.future;
      if (!next) return;
      set({
        shapes: next.shapes,
        order: next.order,
        selection: next.selection,
        isDirty: true,
        history: {
          past: [...state.history.past, snapshotOf(state)],
          future
        }
      });
    },

    addShapes: (shapes, addOptions = {}) =>
      get().commit((document) => addShapes(document, shapes), {
        selection: addOptions.select
          ? shapes.map((shape) => shape.id)
          : undefined
      }),

    updateShape: (id, patch, updateOptions) =>
      get().commit((document) => updateShape(document, id, patch), {
        history: updateOptions?.history
      }),

    moveShapes: (ids, dx, dy) =>
      get().commit((document) => moveShapes(document, ids, dx, dy)),

    deleteSelection: () => {
      const { selection, commit } = get();
      commit((document) => deleteShapes(document, selection), {
        selection: []
      });
    },

    duplicateSelection: (offset) =>
      get().duplicateShapes(get().selection, { offset }),

    duplicateShapes: (ids, duplicateOptions) => {
      let newIds: string[] = [];
      get().commit((document) => {
        const result = duplicateShapes(document, ids, duplicateOptions);
        newIds = result.newIds;
        return result.document;
      });
      if (newIds.length > 0) set({ selection: newIds });
      return newIds;
    },

    reorderSelection: (direction) => {
      const { selection, commit } = get();
      commit((document) => reorderShapes(document, selection, direction));
    },

    groupSelection: () => {
      const { selection, commit } = get();
      let groupId: string | null = null;
      commit((document) => {
        const result = groupShapes(document, selection);
        groupId = result.groupId;
        return result.document;
      });
      if (groupId) set({ selection: [groupId] });
    },

    ungroupSelection: () => {
      const { selection, shapes, commit } = get();
      const groupIds = selection.filter((id) => shapes[id]?.type === "group");
      if (groupIds.length === 0) return;
      let childIds: string[] = [];
      commit((document) =>
        groupIds.reduce((current, groupId) => {
          const result = ungroupShape(current, groupId);
          childIds = [...childIds, ...result.childIds];
          return result.document;
        }, document)
      );
      set({ selection: childIds });
    },

    toggleLocked: (ids) =>
      get().commit((document) =>
        ids.reduce((current, id) => {
          const shape = current.shapes[id];
          return shape
            ? updateShape(current, id, { locked: !shape.locked })
            : current;
        }, document)
      ),

    toggleHidden: (ids) =>
      get().commit((document) =>
        ids.reduce((current, id) => {
          const shape = current.shapes[id];
          return shape
            ? updateShape(current, id, { hidden: !shape.hidden })
            : current;
        }, document)
      ),

    copySelection: () => {
      const { selection, shapes, order } = get();
      const copied = order
        .filter((id) => selection.includes(id))
        .map((id) => shapes[id])
        .filter((shape): shape is Shape => !!shape);
      set({ clipboard: copied });
    },

    pasteClipboard: (offset = { x: 20, y: 20 }) => {
      const { clipboard, commit } = get();
      if (clipboard.length === 0) return [];
      // Paste from a scratch document holding only the clipboard so the
      // duplicate operation (fresh ids, companions, offsets) is reused.
      const scratch: DocumentState = {
        shapes: Object.fromEntries(clipboard.map((shape) => [shape.id, shape])),
        order: clipboard.map((shape) => shape.id)
      };
      const { document: pasted, newIds } = duplicateShapes(
        scratch,
        scratch.order,
        { offset }
      );
      const added = newIds
        .map((id) => pasted.shapes[id])
        .filter((shape): shape is Shape => !!shape);
      commit((document) => addShapes(document, added), { selection: newIds });
      // Repeated pastes step further away each time.
      set({ clipboard: added });
      return newIds;
    }
  }));
};
